import { StyledWhatsNewModal } from './styles'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Grid } from '@enterprise-ui/canvas-ui-react'
import { Separator, Button } from '@design-systems/canvas-ui'
import useGlobalWhatsNew from '../../../domain/useGlobalWhatsNew'
import useUpdateGlobalWhatsNew from '../../../domain/useUpdateGlobalWhatsNew'
import { GlobalWhatsNewStep } from '../../../types/globalWhatsNew'
import { trackEvent } from '../../../common/fireflyHelper'

const MediaContent = ({
  media_type,
  content_url,
  title,
  isVisible,
}: Pick<GlobalWhatsNewStep, 'media_type' | 'content_url' | 'title'> & {
  isVisible: boolean
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const className = isVisible ? undefined : 'whats-new-media-hidden'

  useEffect(() => {
    if (isVisible && videoRef.current) {
      videoRef.current.currentTime = 0
      Promise.resolve(videoRef.current.play()).catch(() => {})
    } else if (!isVisible && videoRef.current) {
      videoRef.current.pause()
    }
  }, [isVisible])

  switch (media_type) {
    case 'video':
      return (
        <div className={className}>
          <video
            ref={videoRef}
            src={content_url}
            muted
            preload="auto"
            className="whats-new-media"
            aria-label={title}
            controls={isVisible}
          />
        </div>
      )
    case 'image':
    case 'gif':
    default:
      return (
        <div className={className}>
          <img src={content_url} alt={title} className="whats-new-media" />
        </div>
      )
  }
}

interface GlobalWhatsNewProps {
  onModalClose?: () => void
}

const GlobalWhatsNew = ({ onModalClose }: GlobalWhatsNewProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [firstMediaLoaded, setFirstMediaLoaded] = useState(false)
  const { data: globalWhatsNewData } = useGlobalWhatsNew()
  const { mutate: updateGlobalWhatsNew } = useUpdateGlobalWhatsNew()

  const hasExplicitCloseRef = useRef(false)
  const stepsRef = useRef<GlobalWhatsNewStep[]>([])
  const isLastStepRef = useRef(false)

  const globalWhatsNewSteps = globalWhatsNewData?.media_list ?? []
  const isLastStep = currentStepIndex === globalWhatsNewSteps.length - 1

  const hasNotifiedNoDataRef = useRef(false)
  useEffect(() => {
    if (
      globalWhatsNewData &&
      !globalWhatsNewSteps.length &&
      !hasNotifiedNoDataRef.current
    ) {
      hasNotifiedNoDataRef.current = true
      onModalClose?.()
    }
  }, [globalWhatsNewData, globalWhatsNewSteps.length, onModalClose])

  useEffect(() => {
    stepsRef.current = globalWhatsNewSteps
    isLastStepRef.current = isLastStep
  }, [globalWhatsNewSteps, isLastStep])

  useEffect(() => {
    const steps = globalWhatsNewData?.media_list
    if (!steps?.length) return

    let isCancelled = false
    const firstStep = steps[0]

    const handleFirstLoaded = () => {
      if (isCancelled) return
      setFirstMediaLoaded(true)
    }

    if (firstStep.media_type === 'video') {
      const video = document.createElement('video')
      video.preload = 'auto'
      video.addEventListener('loadeddata', handleFirstLoaded, { once: true })
      video.addEventListener('error', handleFirstLoaded, { once: true })
      video.src = firstStep.content_url
    } else {
      const img = new Image()
      img.onload = handleFirstLoaded
      img.onerror = handleFirstLoaded
      img.src = firstStep.content_url
    }

    return () => {
      isCancelled = true
    }
  }, [globalWhatsNewData])

  const buildPayload = useCallback(
    (
      steps: GlobalWhatsNewStep[],
      action: 'skip' | 'dismiss' | 'skip_today',
    ) => {
      return steps.map((step) => {
        const payload: {
          media_id: string
          skip_count: number
          is_dismissed: boolean
          last_skipped_date?: string
        } = {
          media_id: step.media_id,
          skip_count:
            action === 'dismiss'
              ? (step.skip_count ?? 0)
              : (step.skip_count ?? 0) + 1,
          is_dismissed: action === 'dismiss',
        }
        if (action === 'skip_today') {
          payload.last_skipped_date = new Date().toISOString().split('T')[0]
        }
        return payload
      })
    },
    [],
  )

  const handleClose = useCallback(
    (action: 'skip' | 'dismiss' | 'skip_today') => {
      if (hasExplicitCloseRef.current) return
      hasExplicitCloseRef.current = true

      if (action === 'dismiss') {
        trackEvent("Global what's new - Tour completed")
      } else if (action === 'skip') {
        trackEvent("Global what's new - Tour skipped", {
          stepsCompleted: currentStepIndex,
        })
      } else if (action === 'skip_today') {
        trackEvent("Global what's new - Don't show today clicked")
      }

      const payload = buildPayload(globalWhatsNewSteps, action)
      if (payload.length) {
        updateGlobalWhatsNew(payload)
      }
      setIsOpen(false)
      onModalClose?.()
    },
    [buildPayload, globalWhatsNewSteps, updateGlobalWhatsNew],
  )

  // Auto-close after 15 minutes (no patch call)
  useEffect(() => {
    const timer = setTimeout(
      () => {
        hasExplicitCloseRef.current = true
        setIsOpen(false)
        onModalClose?.()
      },
      15 * 60 * 1000,
    )
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const handleUnloadOrUnmount = () => {
      if (hasExplicitCloseRef.current || !stepsRef.current.length) return
      hasExplicitCloseRef.current = true
      const action = isLastStepRef.current ? 'dismiss' : 'skip'
      const payload = buildPayload(stepsRef.current, action)
      updateGlobalWhatsNew(payload)
    }

    window.addEventListener('beforeunload', handleUnloadOrUnmount)
    return () => {
      window.removeEventListener('beforeunload', handleUnloadOrUnmount)
      handleUnloadOrUnmount()
    }
  }, [buildPayload, updateGlobalWhatsNew])

  if (!globalWhatsNewSteps.length || !isOpen || !firstMediaLoaded) return null

  return (
    <StyledWhatsNewModal
      headingText="What's New"
      isOpen={isOpen}
      onClose={() => handleClose(isLastStep ? 'dismiss' : 'skip')}
    >
      <Grid.Container justify="center" align="center" direction="column">
        <Grid.Item className="hc8-fs-350">
          <strong>{globalWhatsNewSteps[currentStepIndex]?.title}</strong>
        </Grid.Item>
        <Grid.Item className="hc8-fs-100 hc8-ta-center global-whats-new-description">
          {globalWhatsNewSteps[currentStepIndex]?.description}
        </Grid.Item>
        <Grid.Item>
          {globalWhatsNewSteps.map((step, index) => (
            <MediaContent
              key={step.media_id}
              media_type={step.media_type}
              content_url={step.content_url}
              title={step.title}
              isVisible={index === currentStepIndex}
            />
          ))}
        </Grid.Item>
        <Grid.Item className="hc8-fs-100">{`${currentStepIndex + 1}/${globalWhatsNewSteps.length}`}</Grid.Item>
        <Separator />
        <Grid.Container
          justify="space-between"
          align="center"
          className="hc8-ph-4x hc8-pv-3x"
        >
          <Grid.Item>
            <Button onClick={() => handleClose('skip_today')} variant="ghost">
              Don't show this again today
            </Button>
          </Grid.Item>
          <Grid.Item>
            <Button
              onClick={() => {
                if (isLastStep) {
                  handleClose('dismiss')
                } else {
                  setCurrentStepIndex((prev) => prev + 1)
                }
              }}
              variant="primary"
            >
              {isLastStep ? 'Done' : 'Next'}
            </Button>
          </Grid.Item>
        </Grid.Container>
      </Grid.Container>
    </StyledWhatsNewModal>
  )
}

export default GlobalWhatsNew
